import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { BUSINESS, NAV_LINKS, WHATSAPP_URL } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";
import { useEnquiry } from "@/context/EnquiryContext";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const { pathname } = useLocation();

  const isActive = (to) => {
    const base = to.split("#")[0];
    return base === "/" ? pathname === "/" : pathname.startsWith(base);
  };

  const requestQuote = (source) => {
    setOpen(false);
    openEnquiry({ source });
  };

  return (
    <>
      {/* Compact information strip */}
      <div className="bg-spice-green-dark text-spice-mist text-xs py-2 px-4 font-nav" data-testid="topbar">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <span className="font-medium text-spice-gold" data-testid="topbar-tagline">
            Wholesale Spice Supply | Hyderabad
          </span>
          <span className="hidden nav:inline text-[11px] text-spice-sage" data-testid="topbar-gstin">
            GSTIN: {BUSINESS.gstin} • Hours: {BUSINESS.hours}
          </span>
        </div>
      </div>

      {/* Main navigation */}
      <nav
        className="sticky top-0 z-40 bg-[#FAF8F2] border-b border-spice-line font-nav"
        data-testid="main-nav"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[76px] nav:h-[96px] xl:h-[110px] flex items-center justify-between gap-4 xl:gap-8">
          <Link to="/" className="flex items-center shrink-0" aria-label={`${BUSINESS.name} home`} data-testid="nav-logo">
            <img
              src={BUSINESS.logo}
              alt={`${BUSINESS.name} — ${BUSINESS.tagline}`}
              data-testid="nav-logo-image"
              width="1600"
              height="582"
              className="w-[178px] h-[65px] nav:w-[210px] nav:h-[76px] xl:w-[280px] xl:h-[102px] object-contain mix-blend-multiply select-none"
            />
          </Link>

          <div className="hidden nav:flex items-center gap-4 xl:gap-7 text-[15px] font-medium text-[#24342C]">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.testid}
                to={link.to}
                data-testid={link.testid}
                aria-current={isActive(link.to) ? "page" : undefined}
                className={`relative py-1 transition-colors hover:text-[#174F35] after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:bg-[#63B744] after:transition-all after:duration-300 ${
                  isActive(link.to) ? "text-[#174F35] after:w-full" : "after:w-0 hover:after:w-full"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => requestQuote("header")}
              data-testid="nav-quote-button"
              className="hidden nav:inline-flex items-center justify-center whitespace-nowrap px-4 xl:px-6 py-3 bg-[#174F35] text-white text-[15px] font-semibold rounded-[9px] shadow-sm hover:bg-[#123d29] hover:shadow-md transition-all duration-200"
            >
              Request Quote
            </button>
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="nav:hidden inline-flex items-center justify-center w-11 h-11 rounded-[9px] border border-spice-line text-[#174F35] hover:bg-spice-sand transition-colors"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="nav:hidden border-t border-spice-line bg-[#FAF8F2] px-4 sm:px-6 py-4 flex flex-col gap-1 text-[15px] font-medium text-[#24342C]"
            data-testid="nav-mobile-menu"
          >
            <button
              type="button"
              onClick={() => requestQuote("mobile-menu")}
              data-testid="nav-quote-button-mobile"
              className="mb-2 inline-flex items-center justify-center whitespace-nowrap px-6 py-3 bg-[#174F35] text-white text-[15px] font-semibold rounded-[9px] hover:bg-[#123d29] transition-colors"
            >
              Get Current Quote
            </button>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.testid}
                to={link.to}
                data-testid={`${link.testid}-mobile`}
                onClick={() => setOpen(false)}
                className="px-2 py-3 rounded-[9px] hover:bg-spice-sand hover:text-[#174F35] transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Enquire on WhatsApp (opens WhatsApp in a new tab)"
              data-testid="nav-whatsapp-button-mobile"
              onClick={() => {
                track(EVENTS.whatsappClick, { source: "mobile-menu" });
                setOpen(false);
              }}
              className="mt-1 inline-flex items-center gap-2 px-2 py-3 rounded-[9px] text-[#174F35] font-semibold hover:bg-spice-sand transition-colors"
            >
              <MessageCircle className="w-4 h-4" aria-hidden="true" />
              Enquire on WhatsApp
            </a>
          </div>
        )}
      </nav>
    </>
  );
};
